// -------------- import external dependencies ---------------
import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";

// ----------- import internal dependencies --------------
import { setPassword } from "../../../api/authApi";
import { clearStorage, useReportWrapper } from "../../../store/store";
import { NewPasswordValidator } from "../../../utils/validators/auth";
import AuthLayout from "../../../layouts/authLayout/AuthLayout";
import FormInput from "../../../components/Forms/input/FormInput";
import FormErrors from "../../../components/FormErrors";
import {
  FormWrapper,
  FormHeaderText,
  FormSubTitle,
  Button,
} from "../styles/auth";
import {
  upperCaseReg,
  specialCharsReg,
  numberReg,
} from "../../../shared/sharedConstants";

const INVALID_LINK =
  "This link is no longer valid. It may have expired or already been used. " +
  "Ask your administrator to send a new one.";

/** Landing page for the emailed invite: /set-password?u=<username>&token=<token> */
const SetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // No '+'/space repair here (unlike HandlePassword) because the API link is escaped.
  const username = searchParams.get("u");
  const token = searchParams.get("token");

  const linkIsUsable = Boolean(username && token);

  const { isLoading, mutateAsync: submitPassword } = useMutation((req: any) =>
    setPassword(req)
  );

  const [password_criteria, setPassword_criteria] = useState({
    upperCase: false,
    number: false,
    specialChars: false,
    empty: false,
    eightChars: false,
  });

  const { handleSubmit, values, handleChange, handleBlur, errors } = useFormik({
    initialValues: {
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: NewPasswordValidator(),
    onSubmit: async (values: any) => {
      try {
        await submitPassword({
          username,
          token,
          newPassword: values.newPassword,
        });

        clearStorage();
        useReportWrapper.destroy();
        localStorage.clear();

        navigate("/success?action=password");
      } catch (error: any) {
        // Token problems stay opaque on purpose so this page cannot be used to probe accounts.
        toast.error(error?.response?.data?.error || INVALID_LINK, {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 8000,
          toastId: "set-password",
        });
      }
    },
  });

  useEffect(() => {
    const password = values.newPassword;
    setPassword_criteria({
      upperCase: upperCaseReg.test(password),
      specialChars: specialCharsReg.test(password),
      number: numberReg.test(password),
      empty: password !== "",
      eightChars: password.length >= 8,
    });
  }, [values.newPassword]);

  const handleDisableSubmitBtn = () =>
    isLoading ||
    values.newPassword !== values.confirmPassword ||
    !password_criteria.empty ||
    !password_criteria.upperCase ||
    !password_criteria.number ||
    !password_criteria.specialChars ||
    !password_criteria.eightChars;

  if (!linkIsUsable) {
    return (
      <AuthLayout>
        <FormWrapper>
          <FormHeaderText>Link not valid</FormHeaderText>
          <FormSubTitle>{INVALID_LINK}</FormSubTitle>
        </FormWrapper>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <FormWrapper>
        <FormHeaderText>Set Your Password</FormHeaderText>
        <dl className="mb-10 mt-4 rounded bg-[var(--lightPurpleGrey)] px-4 py-3 max-[1000px]:text-center">
          <dt
            className="text-xs font-medium"
            style={{ color: "var(--text_purple)" }}
          >
            Username
          </dt>
          <dd className="break-all text-xl font-bold text-[color:var(--black)]">
            {username}
          </dd>
        </dl>
        <form onSubmit={handleSubmit}>
          <FormInput
            label={"New Password"}
            name="newPassword"
            type="password"
            value={values.newPassword}
            onChange={handleChange}
            onBlur={handleBlur}
          />

          <FormInput
            label={"Confirm Password"}
            name="confirmPassword"
            type="password"
            value={values.confirmPassword}
            onChange={handleChange}
            onBlur={handleBlur}
          />

          <FormErrors
            empty={password_criteria.empty}
            upperCase={password_criteria.upperCase}
            number={password_criteria.number}
            specialChars={password_criteria.specialChars}
            eightChars={password_criteria.eightChars}
            showMatch
            values={values}
            errors={errors}
            pwToCheck={"confirmPassword"}
          />

          <Button disabled={handleDisableSubmitBtn()}>
            {isLoading ? "Setting..." : "Set Password"}
          </Button>

          <div className="mt-4 text-center text-sm font-medium">
            Need help?{" "}
            <a
              href="https://mitigaterisk.com/contact-us/"
              target="_blank"
              rel="noreferrer"
              className="text-purple-300"
            >
              Contact us
            </a>
          </div>
        </form>
      </FormWrapper>
    </AuthLayout>
  );
};

export default SetPassword;
